/**
 * The CPU and memory a device's last scan used, as the agent measured them
 * while it ran (release 3.2 telemetry item 2), against the CPU ceiling that was
 * in force. This is the number a customer with a CPU ceiling on its laptops
 * asks for: not a reading taken between scans, but the scan itself, document
 * workers included.
 */

import { Cpu } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import type { EdgeScanResources } from '@/lib/api';

import {
  CAP_NOTE,
  ceilingVerdict,
  formatMemory,
  formatPercent,
  methodText,
  NOT_THE_CHECKIN_READING,
  verdictText,
  type CeilingVerdict,
} from './scan-resources-format';

const VERDICT_BADGE: Record<CeilingVerdict, { label: string; variant: 'success' | 'info' | 'critical' | 'glass' }> = {
  under: { label: 'below the cap', variant: 'success' },
  reached: { label: 'at or over the cap', variant: 'info' },
  no_ceiling: { label: 'no cap', variant: 'glass' },
  not_measured: { label: 'too short to measure', variant: 'glass' },
};

function Figure({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="rounded border border-border/60 p-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-sm font-medium tabular-nums" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export function ScanResourcesCard({
  resources,
  finishedAt,
}: {
  /** undefined: the controller does not send it (older than 3.2); null: the agent did not report it. */
  resources: EdgeScanResources | null | undefined;
  finishedAt: string | null;
}) {
  const verdict = resources ? ceilingVerdict(resources) : null;
  return (
    <GlassCard className="p-3" data-testid="scan-resources-card">
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <Cpu className="h-4 w-4" /> Last scan: CPU and memory
          {verdict && (
            <Badge
              variant={VERDICT_BADGE[verdict].variant}
              className="ml-1 text-[10px]"
              data-testid="scan-resources-verdict"
            >
              {VERDICT_BADGE[verdict].label}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-[11px]">
          {finishedAt ? <>Scan finished {new Date(finishedAt).toLocaleString()}. </> : null}
          Measured by the agent while the scan ran, not between scans.
        </CardDescription>
      </CardHeader>
      {!resources ? (
        <p className="text-xs text-muted-foreground" data-testid="scan-resources-not-reported">
          Not reported. Agents from release 3.2 measure each scan; older agents report only the reading taken at
          check-in (above: the agent process alone, as % of one core), which is not a scan measurement.
        </p>
      ) : (
        <div className="space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Figure label="Average CPU" value={formatPercent(resources.cpuAvgPercent)} testId="scan-cpu-avg" />
            <Figure label="Busiest 5 s" value={formatPercent(resources.cpuPeakPercent)} testId="scan-cpu-peak" />
            <Figure
              label="CPU cap in force"
              value={resources.cpuCeilingPercent != null ? `${resources.cpuCeilingPercent}%` : 'none'}
              testId="scan-cpu-ceiling"
            />
            <Figure label="Peak memory" value={formatMemory(resources.memPeakMb)} testId="scan-mem-peak" />
          </div>
          <p className="text-foreground/90" data-testid="scan-resources-verdict-text">
            {verdictText(resources)}
          </p>
          <p className="text-muted-foreground" data-testid="scan-resources-method">
            {methodText(resources)} CPU time used: {resources.cpuSeconds.toFixed(1)} s.
            {resources.cpuCeilingPercent != null ? <> {CAP_NOTE}</> : null} {NOT_THE_CHECKIN_READING}
          </p>
        </div>
      )}
    </GlassCard>
  );
}
